import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * radaccess's OWN operator data — profile, tax, bank, licence.
 *
 * 🔑 THIS PREFIX IS NOT A PROXY. Every other module here reaches a backend
 * through radaccess; these tables live IN radaccess. That matters for one
 * reason: /access/operator is the ONLY prefix where radaccess wires
 * NamespaceIsolationMiddleware, so containment is enforced by the gateway
 * itself rather than by a downstream service.
 */

const PREFIX = "/operator";

/** radaccess `model.OperatorProfile` — app/model/operatorprofile.go:28. */
export interface OperatorProfile {
  namespace: string;
  id?: string;
  legalname: string;
  /**
   * Optional name printed beside the legal name on invoices, "<legal> (trading
   * as <trade>)" (owner 2026-10-29; radaccess v1.211). Must be on the GST
   * registration. Absent on save keeps the stored one; "" clears it.
   */
  tradename?: string;
  /** PIN and FRANCHISE are set only from the kind a parent picks; the profile screen keeps them read-only. */
  /**
   * What radaccess can RETURN. Deliberately wider than OPERATOR_TYPES in
   * src/domain/operator.ts, which is what a person may CHOOSE: CLOUD_TENANT
   * has existed in radaccess since 2026-09-16 and NETWORK_OPERATOR since
   * v1.80, and a profile carrying either must type-check here whether or not
   * the dropdown ever offers it.
   */
  operatortype: "ISP" | "VNO" | "OPERATOR" | "PIN" | "FRANCHISE" | "CLOUD_TENANT" | "NETWORK_OPERATOR";
  upstreamispnamespace?: string;
  allowpureinternetservice: boolean;
  active?: boolean;
}

/**
 * radaccess `model.OperatorTaxInfo`.
 *
 * ⚠️ registrationstatus and filingfrequency reach the API only from
 * radaccess v1.24. Against an older gateway they are accepted, ignored, and
 * the row keeps the column defaults REGULAR/MONTHLY — silently.
 */
export interface OperatorTaxInfo {
  namespace: string;
  gstin: string;
  pan: string;
  registrationstatus: "REGULAR" | "COMPOSITION" | "UNREGISTERED";
  filingfrequency: "MONTHLY" | "QUARTERLY";
  verified?: boolean;
}

/** radaccess `model.OperatorLicense` — new in v1.24. */
export interface OperatorLicense {
  namespace: string;
  holdslicense: boolean;
  licensetype: "UL" | "ISP_A" | "ISP_B" | "ISP_C" | "VNO" | "";
  licensenumber: string;
  licensedentity: string;
  validfrom?: string | null;
  validto?: string | null;
  invoiceseriescode: string;
  verified?: boolean;
  /**
   * Whose name is on this operator's pure-internet invoices — radaccess
   * v1.65's `subscriber_invoice_identity`.
   *
   * 🔑 It is DERIVED here, not typed by the operator: naming an upstream IS
   * the question. See `invoiceIdentityFor`. Omitting it on save leaves
   * whatever the row already holds, so an older console cannot reset it.
   */
  subscriberinvoiceidentity?: "own" | "branch_of_upstream";
}

/**
 * Whose name goes on this operator's internet invoices.
 *
 * 🔑 THE UPSTREAM FIELD IS ALREADY THE UI FOR THIS. An operator that names an
 * upstream is saying "they invoice my subscribers for me" — the GST branch
 * relationship. One that names none is saying "I invoice them myself". So
 * there is no new control to add and no new question to ask; the answer is
 * read off a field the form already has.
 *
 * ⚠️ Until 2026-09-16 this was derived from `holdslicense` in radbilling, and
 * an operator holding no licence ALWAYS meant the upstream invoiced. That is
 * no longer true: an unlicensed operator buying capacity per subscriber
 * invoices in its own name. Licence and invoice identity are separate facts.
 */
export function invoiceIdentityFor(
  p: OperatorProfile,
): "own" | "branch_of_upstream" {
  return (p.upstreamispnamespace ?? "").trim()
    ? "branch_of_upstream"
    : "own";
}

const ns = (n: string) => encodeURIComponent(n);

export const getProfile = (i: CallerIdentity, n: string) =>
  callGateway<OperatorProfile>(i, config().gatewayBase, `${PREFIX}/${ns(n)}`);

export const createProfile = (i: CallerIdentity, p: OperatorProfile) =>
  callGateway<OperatorProfile>(i, config().gatewayBase, `${PREFIX}/${ns(p.namespace)}`, {
    method: "POST",
    body: p,
  });

export const updateProfile = (i: CallerIdentity, p: OperatorProfile) =>
  callGateway<OperatorProfile>(i, config().gatewayBase, `${PREFIX}/${ns(p.namespace)}`, {
    method: "PUT",
    body: p,
  });

export const getTaxInfo = (i: CallerIdentity, n: string) =>
  callGateway<OperatorTaxInfo>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/taxinfo`);

export const saveTaxInfo = (i: CallerIdentity, t: OperatorTaxInfo, exists: boolean) =>
  callGateway<OperatorTaxInfo>(i, config().gatewayBase, `${PREFIX}/${ns(t.namespace)}/taxinfo`, {
    method: exists ? "PUT" : "POST",
    body: t,
  });

export const getLicense = (i: CallerIdentity, n: string) =>
  callGateway<OperatorLicense>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/license`);

export const saveLicense = (i: CallerIdentity, l: OperatorLicense, exists: boolean) =>
  callGateway<OperatorLicense>(i, config().gatewayBase, `${PREFIX}/${ns(l.namespace)}/license`, {
    method: exists ? "PUT" : "POST",
    body: l,
  });

export const EMPTY_PROFILE = (namespace: string): OperatorProfile => ({
  namespace,
  legalname: "",
  operatortype: "OPERATOR",
  upstreamispnamespace: "",
  allowpureinternetservice: false,
});

export const EMPTY_TAXINFO = (namespace: string): OperatorTaxInfo => ({
  namespace,
  gstin: "",
  pan: "",
  registrationstatus: "REGULAR",
  filingfrequency: "MONTHLY",
});

export const EMPTY_LICENSE = (namespace: string): OperatorLicense => ({
  namespace,
  holdslicense: false,
  licensetype: "",
  licensenumber: "",
  licensedentity: "",
  validfrom: null,
  validto: null,
  invoiceseriescode: "",
});

/**
 * 🔑 THE COHERENCE RULE THAT SPANS THE THREE RECORDS, checked before saving.
 *
 * radaccess validates tax and licence each on its own, and cannot see the
 * other: taxinfo does not know whether a licence exists, and the licence does
 * not know the operator's upstream. Nothing downstream catches an incoherent
 * pair; it surfaces as an invoice with the wrong supplier, months later.
 *
 * 🔄 REWRITTEN 2026-09-16. This used to reject "no licence AND no upstream" as
 * naming nobody who may issue the document. That is no longer true and the
 * rule was BLOCKING A REAL CONFIGURATION: an unlicensed operator buying
 * capacity per subscriber (a "pin") invoices its own subscribers in its own
 * name and has no upstream to name. It is how most franchised operators
 * actually run. What the rule now checks is that the operator has answered the
 * question at all, and that its answers do not contradict each other.
 */
export function crossRecordProblems(
  p: OperatorProfile,
  l: OperatorLicense,
): string[] {
  const problems: string[] = [];
  const upstream = (p.upstreamispnamespace ?? "").trim();

  if (l.holdslicense && upstream) {
    problems.push(
      "This operator holds its own licence, so an upstream provider is not needed — internet " +
        "service is invoiced in its own name. Clear the upstream, or clear the licence.",
    );
  }
  if (upstream && upstream === p.namespace) {
    problems.push("The upstream provider cannot be the operator itself.");
  }
  // 🔄 The pure-internet flag means "may invoice internet service IN ITS OWN
  // NAME". So what it contradicts is naming an upstream, not lacking a
  // licence: an operator whose upstream invoices for it does not issue that
  // document at all. Lacking a licence while invoicing in its own name is a
  // real, common configuration -- a franchised operator buying capacity per
  // subscriber -- and the platform does not adjudicate it (owner, 2026-09-16).
  if (p.allowpureinternetservice && upstream) {
    problems.push(
      "Pure internet service is allowed for this operator, but it names an upstream provider — " +
        "so its internet is invoiced in the upstream's name, not its own. Clear one of the two.",
    );
  }
  return problems;
}

/** radaccess `model.OperatorAddress` — app/model/operatorcontact.go:19. */
export interface OperatorAddress {
  namespace: string;
  addresstype: string;
  address: string;
  city: string;
  state: string;
  pin: string;
  country: string;
  verified?: boolean;
}

/**
 * The address type every reader of a provider's legal address looks for.
 *
 * 🚨 radbilling builds the supplier AND the operator buyer of every invoice
 * from `address_type = 'registered_office'` (gstparty.go, buyertype.go), and
 * all 8 such rows in the database used that spelling (measured 2026-09-28).
 * This screen wrote and read "registered" instead, so an address saved here
 * was stored and then never found: the invoice failed for a missing address
 * the operator had just typed, and an existing address showed as blank.
 */
export const REGISTERED_ADDRESS_TYPE = "registered_office";

/** The legacy spelling this screen once wrote. Read, never written. */
const LEGACY_REGISTERED_ADDRESS_TYPE = "registered";

/**
 * A provider's registered address from its address list: the canonical row
 * if there is one, else a legacy "registered" row so it is at least SHOWN.
 */
export function findRegisteredAddress(list: OperatorAddress[] | null | undefined): OperatorAddress | undefined {
  const all = list ?? [];
  return (
    all.find((a) => a.addresstype === REGISTERED_ADDRESS_TYPE) ??
    all.find((a) => a.addresstype === LEGACY_REGISTERED_ADDRESS_TYPE)
  );
}

export const EMPTY_ADDRESS = (namespace: string): OperatorAddress => ({
  namespace,
  addresstype: REGISTERED_ADDRESS_TYPE,
  address: "",
  city: "",
  state: "",
  pin: "",
  country: "India",
});

export const listAddresses = (i: CallerIdentity, n: string) =>
  callGateway<OperatorAddress[]>(i, config().gatewayBase, `${PREFIX}/${ns(n)}/address`);

export const createAddress = (i: CallerIdentity, a: OperatorAddress) =>
  callGateway<OperatorAddress>(i, config().gatewayBase, `${PREFIX}/${ns(a.namespace)}/address`, {
    method: "POST",
    body: a,
  });

export const updateAddress = (i: CallerIdentity, a: OperatorAddress) =>
  callGateway<OperatorAddress>(
    i,
    config().gatewayBase,
    `${PREFIX}/${ns(a.namespace)}/address/${encodeURIComponent(a.addresstype)}`,
    { method: "PUT", body: a },
  );

/**
 * The upstream provider, captured on the DOWNSTREAM operator's form and saved
 * as the UPSTREAM's own operator record.
 *
 * 🔑 WHY IT LANDS THERE AND NOT HERE. An unlicensed operator's internet
 * service is invoiced in the licence holder's name, and radbilling resolves
 * that by walking UP the namespace tree and reading THAT namespace's own
 * profile, taxinfo and address (supplierofrecord.go resolveFromPrefix, then
 * gstparty.go resolveSupplierIdentity). Storing the upstream's GSTIN on the
 * downstream operator would put it somewhere nothing reads — and would copy
 * one company's legal identity onto every reseller beneath it, so a GSTIN
 * change would have to be edited in N places or drift in N-1 of them.
 *
 * ⛔ CONSEQUENCE, AND IT IS THE RIGHT ONE: an operator CANNOT create its own
 * upstream's record. IsNamespaceAccessible admits a namespace only at or below
 * the caller's own, and the upstream is strictly ABOVE. So this write is
 * refused 403 for the reseller and succeeds only for someone at or above the
 * upstream. That is correct — the record is another company's legal identity,
 * not the reseller's to assert.
 */
export interface UpstreamDraft {
  namespace: string;
  legalname: string;
  pan: string;
  gstin: string;
  registrationstatus: OperatorTaxInfo["registrationstatus"];
  licensetype: OperatorLicense["licensetype"];
  licensenumber: string;
  licensedentity: string;
  validfrom: string;
  validto: string;
  address: string;
  city: string;
  state: string;
  pin: string;
}

export const EMPTY_UPSTREAM: UpstreamDraft = {
  namespace: "",
  legalname: "",
  pan: "",
  gstin: "",
  registrationstatus: "REGULAR",
  licensetype: "",
  licensenumber: "",
  licensedentity: "",
  validfrom: "",
  validto: "",
  address: "",
  city: "",
  state: "",
  pin: "",
};

/**
 * 🚨 THE UPSTREAM MUST BE A NAMESPACE ANCESTOR, AND radbilling ENFORCES IT.
 * resolveFromPrefix returns ErrUpstreamNotAncestor for anything else, so an
 * upstream that is an unrelated outside company is refused at invoice time —
 * long after this form accepted it. Refusing here makes that failure visible
 * where it can be fixed, rather than months later on a document.
 */
export function isAncestorOf(candidate: string, namespace: string): boolean {
  if (!candidate) return false;
  return candidate === namespace || namespace.startsWith(candidate + ".");
}

/**
 * Whether a provider of this type invoices internet service to anyone.
 *
 * 🔑 MIRRORS radaccess v1.80 (c815894) model.SellsInternetService. The two must
 * agree: this one decides whether Save is allowed, the server's decides whether
 * APPROVAL is allowed, and a screen that lets someone save what the server will
 * refuse is worse than one that refuses early.
 *
 * It is derived from the TYPE and must never become something the provider can
 * tick. The upstream rule exists to stop an unlicensed provider invoicing
 * internet in a company's name that never agreed to supply it; a self-set
 * "I don't sell internet" flag would let it switch off its own gate.
 *
 * PIN and FRANCHISE return true on purpose -- they DO invoice internet service,
 * in the parent's name, so they still need a real upstream. An unknown or unset
 * type returns true, so an unclassified profile fails safe.
 */
export function sellsInternetService(operatorType: string): boolean {
  return operatorType !== "NETWORK_OPERATOR" && operatorType !== "CLOUD_TENANT";
}

/** The named upstream is the wrong namespace: itself, or not a parent. */
function upstreamIdentityErrors(operatorNs: string, un: string): string[] {
  if (un === operatorNs) return ["The upstream cannot be the operator itself."];
  if (!isAncestorOf(un, operatorNs)) {
    return [
      `"${un}" is not an ancestor of "${operatorNs}". radbilling refuses a non-ancestor upstream ` +
        "(ErrUpstreamNotAncestor), so an invoice for this operator would fail rather than pick the " +
        "wrong supplier. Use the parent namespace, or record this as an outside licence holder — " +
        "which the billing resolution does not support yet.",
    ];
  }
  return [];
}

/**
 * The upstream problems that must stop a SAVE: only a namespace that is
 * WRONG -- the operator itself, or one that is not its parent. Saving either
 * records a falsehood that billing would later reject.
 *
 * 🔑 A MISSING upstream, or an upstream whose own legal name, PAN, address or
 * licence is not on file yet, does NOT stop a save (owner 2026-09-28: "allow to
 * save it without upstream provider"). That is an INCOMPLETE profile, not a
 * wrong one, and it is already refused where it matters: radaccess
 * blockersFrom() will not APPROVE an unlicensed provider that invoices internet
 * service without a real, approved upstream. Blocking the save as well only
 * stopped people recording what they have -- and deadlocked a PIN operator,
 * whose upstream's details cannot be saved while the upstream's own profile is
 * blocked for the same reason.
 */
export function upstreamSaveErrors(operatorNs: string, u: UpstreamDraft): string[] {
  const un = u.namespace.trim().toLowerCase();
  if (!un) return [];
  return upstreamIdentityErrors(operatorNs, un);
}

/**
 * The upstream namespace Edit profile writes the upstream's own records at,
 * or null when there is nothing to write: the operator holds a licence, does
 * not invoice internet, or has not named an upstream yet. A missing upstream
 * is saved and reported as an approval need (upstreamProblems), so it must not
 * be written -- with a blank name the writes went to "/operator/" and the
 * whole save failed 404 (testisp, owner 2026-10-28).
 */
export function upstreamToWrite(holdsLicense: boolean, operatorType: string, u: UpstreamDraft): string | null {
  const un = u.namespace.trim().toLowerCase();
  return !holdsLicense && sellsInternetService(operatorType) && un ? un : null;
}

export function upstreamProblems(operatorNs: string, u: UpstreamDraft): string[] {
  const p: string[] = [];
  const un = u.namespace.trim().toLowerCase();
  if (!un) {
    p.push("An upstream namespace is required — it is where the upstream's details are stored.");
    return p;
  }
  p.push(...upstreamIdentityErrors(operatorNs, un));
  if (!u.legalname.trim()) p.push("The upstream's legal name is required — it prints on the invoice.");
  if (!u.pan.trim()) p.push("The upstream's PAN is required.");
  if (!u.address.trim() || !u.city.trim() || !u.state.trim())
    p.push("The upstream's registered address, city and state are required — Rule 46 mandates them.");
  if (!u.licensenumber.trim())
    p.push("The upstream's licence number is required — it is why it may invoice internet service.");
  if (!u.licensetype) p.push("The upstream's licence type is required.");
  return p;
}
